import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { WAIVER_DOCUMENT, ALL_WAIVER_CLAUSE_IDS, medicalAnswersFromChecklist } from '@/domain/waiverDocument';
import { customerName } from '@/utils/format';
import type { CustomerRecord, Booking, WaiverAnswers, WaiverFoundVia } from '@/domain/types';
import HoldToSign from '@/components/HoldToSign';
import { Camera } from 'lucide-react';

/** Who is signing: the name pre-fills the form; the desk also pre-fills contact.
 *  The public page passes the first name only (tv-k9b.11). */
type SigningCustomer = Pick<CustomerRecord, 'firstName' | 'lastName'> &
  Partial<Pick<CustomerRecord, 'preferredName' | 'dateOfBirth' | 'address' | 'phone' | 'email'>>;

interface Props {
  customer: SigningCustomer;
  booking?: Booking;
  variant?: 'customer' | 'desk';
  onSigned: (answers: WaiverAnswers) => void;
}

const FOUND_VIA: Array<{ value: WaiverFoundVia; label: string }> = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'walk-by', label: 'Walk-by' },
  { value: 'friend', label: 'Friend' },
];

function parseList(text: string): string[] {
  return text.split(',').map((t) => t.trim()).filter(Boolean);
}

export default function WaiverAgreementForm({ customer, variant = 'customer', onSigned }: Props) {
  const isDesk = variant === 'desk';
  const who = isDesk ? 'Customer' : 'You';

  const [conditions, setConditions] = useState<Set<string>>(new Set());
  const [allergies, setAllergies] = useState('');
  const [other, setOther] = useState('');
  const [agreedAll, setAgreedAll] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [photoConsent, setPhotoConsent] = useState(true);
  const [foundVia, setFoundVia] = useState<WaiverFoundVia | undefined>(undefined);

  const [fullName, setFullName] = useState(customerName(customer).trim());
  const [dob, setDob] = useState(customer.dateOfBirth ?? '');
  const [address, setAddress] = useState(customer.address ?? '');
  const [phone, setPhone] = useState(customer.phone ?? '');
  const [email, setEmail] = useState(customer.email ?? '');
  const [signatureName, setSignatureName] = useState('');

  const canSign = agreedAll && ageConfirmed && signatureName.trim().length > 0;

  const toggleCondition = (id: string) =>
    setConditions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const submit = () => {
    const medical = medicalAnswersFromChecklist({
      conditionIds: [...conditions],
      allergies: parseList(allergies),
      other,
    });
    const answers: WaiverAnswers = {
      ...medical,
      ageConfirmed,
      consent: agreedAll,
      acknowledgedClauseIds: ALL_WAIVER_CLAUSE_IDS,
      photoConsent,
      foundVia,
      signatureName: signatureName.trim(),
      signedIdentity: {
        fullName: fullName.trim(),
        dateOfBirth: dob.trim() || undefined,
        address: address.trim() || undefined,
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
      },
    };
    onSigned(answers);
  };

  const clauses = useMemo(() => WAIVER_DOCUMENT.clauses, []);

  return (
    <div className="space-y-5" data-testid="waiver-agreement-form">
      {/* The agreement itself */}
      <section
        className="panel-dark max-h-64 overflow-y-auto p-4 space-y-3"
        data-testid="waiver-agreement-text"
        tabIndex={0}
      >
        <p className="font-serif text-sm text-cream leading-relaxed">{WAIVER_DOCUMENT.preamble}</p>
        <p className="font-serif text-[13px] text-cream-soft leading-relaxed">
          {WAIVER_DOCUMENT.riskDisclosure}
        </p>
        <ol className="space-y-2 list-decimal pl-4">
          {clauses.map((c) => (
            <li key={c.id} className="font-serif text-[13px] text-cream-soft leading-relaxed">
              {c.text}
            </li>
          ))}
        </ol>
        <p className="font-serif text-[12px] text-cream-faint italic leading-relaxed">
          {WAIVER_DOCUMENT.closing}
        </p>
      </section>

      {/* Medical declaration — negative checklist */}
      <section className="space-y-2">
        <p className="label-small-caps text-gold-bright">Medical declaration</p>
        <p className="font-serif text-[12px] text-cream-soft">{WAIVER_DOCUMENT.medicalIntro}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {WAIVER_DOCUMENT.medicalConditions.map((m) => (
            <label
              key={m.id}
              className="flex items-center gap-2 font-serif text-sm text-cream cursor-pointer"
            >
              <input
                type="checkbox"
                checked={conditions.has(m.id)}
                onChange={() => toggleCondition(m.id)}
                data-testid={`waiver-cond-${m.id}`}
                className="accent-gold-bright"
              />
              {m.label}
            </label>
          ))}
        </div>
        <Field label="Allergies" placeholder="latex, lidocaine — leave blank if none" value={allergies} onChange={setAllergies} testid="waiver-allergies" />
        <Field label="Anything else we should know?" placeholder="Optional" value={other} onChange={setOther} testid="waiver-other" />
      </section>

      {/* Photo release (opt-out) */}
      <label className="flex items-start gap-2 font-serif text-sm text-cream cursor-pointer">
        <input
          type="checkbox"
          checked={photoConsent}
          onChange={(e) => setPhotoConsent(e.target.checked)}
          className="mt-1 accent-gold-bright"
          data-testid="waiver-photo-consent"
        />
        <span className="flex items-center gap-1.5">
          <Camera size={13} className="text-gold-dim" />
          {isDesk ? 'Customer is happy to be' : "I'm happy to be"} photographed for the studio&apos;s
          portfolio <span className="text-cream-faint">(untick to opt out)</span>
        </span>
      </label>

      {/* How did you find us */}
      <section className="space-y-2">
        <p className="label-small-caps text-gold-bright">How did {isDesk ? 'they' : 'you'} find us?</p>
        <div className="flex flex-wrap gap-2">
          {FOUND_VIA.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setFoundVia((cur) => (cur === f.value ? undefined : f.value))}
              data-testid={`waiver-found-${f.value}`}
              className={clsx(
                'px-3 py-1.5 rounded-md border text-xs transition',
                foundVia === f.value
                  ? 'border-gold-bright bg-gold/10 text-gold-bright'
                  : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/60',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </section>

      {/* Identity */}
      <section className="space-y-2">
        <p className="label-small-caps text-gold-bright">{isDesk ? 'Customer details' : 'Your details'}</p>
        <Field label="Full name" value={fullName} onChange={setFullName} testid="waiver-fullname" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Field label="Date of birth" placeholder="dd/mm/yyyy" value={dob} onChange={setDob} testid="waiver-dob" />
          <Field label="Phone" value={phone} onChange={setPhone} testid="waiver-phone" />
        </div>
        <Field label="Address" value={address} onChange={setAddress} testid="waiver-address" />
        <Field label="Email" value={email} onChange={setEmail} testid="waiver-email" />
      </section>

      {/* Consent + age */}
      <div className="space-y-2">
        <label className="flex items-start gap-2 font-serif text-sm text-cream cursor-pointer">
          <input
            type="checkbox"
            checked={agreedAll}
            onChange={(e) => setAgreedAll(e.target.checked)}
            className="mt-1 accent-gold-bright"
            data-testid="waiver-agree-all"
          />
          {who} {isDesk ? 'has read and agrees' : 'have read and agree'} to all of the above.
        </label>
        <label className="flex items-start gap-2 font-serif text-sm text-cream cursor-pointer">
          <input
            type="checkbox"
            checked={ageConfirmed}
            onChange={(e) => setAgeConfirmed(e.target.checked)}
            className="mt-1 accent-gold-bright"
            data-testid="waiver-age"
          />
          {who} {isDesk ? 'is' : 'are'} 18 years or older (proof of age sighted).
        </label>
      </div>

      {/* Signature */}
      <section className="space-y-2">
        <p className="font-serif text-[12px] text-cream-soft">{WAIVER_DOCUMENT.signatureIntent}</p>
        <Field
          label={isDesk ? 'Signature (type full name)' : 'Signature — type your full name'}
          placeholder="Full name"
          value={signatureName}
          onChange={setSignatureName}
          testid="waiver-signature"
        />
        <HoldToSign onSigned={submit} disabled={!canSign} testid="waiver-hold-to-sign" />
        {!canSign ? (
          <p className="font-inter text-[10px] text-cream-faint text-center uppercase tracking-widest">
            Agree, confirm age &amp; type your name to sign
          </p>
        ) : null}
      </section>
    </div>
  );
}

function Field({
  label,
  placeholder,
  value,
  onChange,
  testid,
}: {
  label: string;
  placeholder?: string;
  value: string;
  onChange: (v: string) => void;
  testid: string;
}) {
  return (
    <div>
      <label className="label-small-caps mb-1 block">{label}</label>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full rounded-md border border-gold-dim/30 bg-ink-black/40 px-3 py-2 font-serif text-sm text-cream placeholder:text-cream-faint focus:border-gold-bright/60 focus:outline-none"
      />
    </div>
  );
}
